import type { Edge, WorkflowNode } from "./model";
import { nodePortTypes, type PortType } from "./components/nodeStyles";

export type WorkflowValidationIssue = {
  edge: Edge;
  edgeKey: string;
  message: string;
  sourceNodeID: string;
  targetNodeID: string;
};

export function workflowEdgeKey(edge: Edge) {
  return `${edge.source}:${edge.sourcePort ?? "output"}->${edge.target}:${edge.targetPort ?? "input"}`;
}

export function connectionTypes(
  source: WorkflowNode,
  sourcePort: string,
  target: WorkflowNode,
  targetPort: string,
) {
  const sourceTypes = nodePortTypes(source, "output", sourcePort);
  const targetTypes = nodePortTypes(target, "input", targetPort);
  return { sourceTypes, targetTypes };
}

export function isConnectionCompatible(
  source: WorkflowNode,
  sourcePort: string,
  target: WorkflowNode,
  targetPort: string,
) {
  const { sourceTypes, targetTypes } = connectionTypes(
    source,
    sourcePort,
    target,
    targetPort,
  );
  if (!sourceTypes.length || !targetTypes.length) return true;
  return sourceTypes.some((type) => targetTypes.includes(type));
}

function typeLabel(types: PortType[]) {
  return types.join(" or ");
}

export function validateWorkflow(
  nodes: WorkflowNode[],
  edges: Edge[],
): WorkflowValidationIssue[] {
  const nodeByID = new Map(nodes.map((node) => [node.id, node]));
  return edges.flatMap((edge) => {
    const source = nodeByID.get(edge.source);
    const target = nodeByID.get(edge.target);
    if (!source || !target) return [];
    const sourcePort = edge.sourcePort ?? "output";
    const targetPort = edge.targetPort ?? "input";
    if (isConnectionCompatible(source, sourcePort, target, targetPort))
      return [];
    const { sourceTypes, targetTypes } = connectionTypes(
      source,
      sourcePort,
      target,
      targetPort,
    );
    return [
      {
        edge,
        edgeKey: workflowEdgeKey(edge),
        message: `${source.title} outputs ${typeLabel(sourceTypes)}, but ${target.title} expects ${typeLabel(targetTypes)}`,
        sourceNodeID: source.id,
        targetNodeID: target.id,
      },
    ];
  });
}
